import type { ContentI18n } from "@/lib/questContent/types";

/** Fixed for v1 (spec: "Simple fixed set"). Order is the on-screen order. */
export const BOARD_COLUMNS = ["backlog", "todo", "in_progress", "waiting", "done"] as const;
export type BoardColumn = (typeof BOARD_COLUMNS)[number];

export const TITLE_MAX = 200;
export const NOTES_MAX = 4000;
export const POSITION_STEP = 1024;

/** The quest/task as the owner can see it right now. null = RLS hides it
 *  (unassigned, cancelled, deleted) — the card falls back to its own title. */
export interface LinkedTarget {
  title: string;
  status: string;
  i18n: ContentI18n | null;
  sourceLang: string | null;
}

export type BoardLinkKind = "quest" | "task";

export interface BoardLink {
  kind: BoardLinkKind;
  id: string;
  target: LinkedTarget | null;
}

/** What gets pinned: the item and its source-language title at pin time. */
export interface PinTarget {
  kind: BoardLinkKind;
  id: string;
  title: string;
}

export interface BoardCard {
  id: string;
  column: BoardColumn;
  position: number;
  title: string;
  notes: string | null;
  dueDate: string | null;
  createdAt: string;
  link: BoardLink | null;
}

/** Fields an edit may change; `undefined` = leave as is. Column and order change via move(). */
export interface BoardCardPatch {
  title?: string;
  notes?: string | null;
  dueDate?: string | null;
}

export interface BoardCardDraft {
  title: string;
  notes?: string | null;
  dueDate?: string | null;
  column?: BoardColumn;
}

export function isBoardColumn(v: unknown): v is BoardColumn {
  return typeof v === "string" && (BOARD_COLUMNS as readonly string[]).includes(v);
}

function compare(a: BoardCard, b: BoardCard): number {
  return a.position - b.position || a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);
}

export function groupByColumn(cards: BoardCard[]): Record<BoardColumn, BoardCard[]> {
  const out = Object.fromEntries(BOARD_COLUMNS.map((c) => [c, [] as BoardCard[]])) as Record<BoardColumn, BoardCard[]>;
  for (const c of cards) out[c.column].push(c);
  for (const col of BOARD_COLUMNS) out[col].sort(compare);
  return out;
}

export function positionBetween(prev: number | undefined, next: number | undefined): number {
  if (prev === undefined && next === undefined) return POSITION_STEP;
  if (prev === undefined) return next! - POSITION_STEP;
  if (next === undefined) return prev + POSITION_STEP;
  return (prev + next) / 2;
}

export function renumber(cards: BoardCard[]): { id: string; position: number }[] {
  return cards.map((c, i) => ({ id: c.id, position: (i + 1) * POSITION_STEP }));
}

/**
 * The board after `id` lands at `index` among `column`'s other cards (clamped;
 * omitted = bottom). The destination column is renumbered 1024, 2048, …, which
 * is exactly what the board_move_card RPC writes, so the optimistic board and
 * the saved one agree without a refetch. Unknown id: the board is unchanged.
 */
export function applyMove(cards: BoardCard[], id: string, column: BoardColumn, index?: number): BoardCard[] {
  const moving = cards.find((c) => c.id === id);
  if (!moving) return cards;
  const others = groupByColumn(cards.filter((c) => c.id !== id))[column];
  const at = Math.max(0, Math.min(index ?? others.length, others.length));
  const order = [...others.slice(0, at), { ...moving, column }, ...others.slice(at)];
  const placed = new Map(renumber(order).map(({ id: cid, position }) => [cid, position]));
  // A card whose column and position don't change stays the same object, like
  // the RPC's IS DISTINCT FROM filter, so callers can tell what a move touched.
  return cards.map((c) => {
    const position = placed.get(c.id);
    if (position === undefined) return c;
    const col = c.id === id ? column : c.column;
    return col === c.column && position === c.position ? c : { ...c, column: col, position };
  });
}

/** Returns an i18n key describing the first problem, or null. */
export function validateDraft(d: BoardCardDraft): string | null {
  const title = d.title.trim();
  if (title.length === 0) return "board.errors.titleRequired";
  if (title.length > TITLE_MAX) return "board.errors.titleTooLong";
  if (d.notes && d.notes.length > NOTES_MAX) return "board.errors.notesTooLong";
  return null;
}

export function normalizeDraft(d: BoardCardDraft): Required<BoardCardDraft> {
  return {
    title: d.title.trim(),
    notes: d.notes?.trim() ? d.notes.trim() : null,
    dueDate: d.dueDate ? d.dueDate : null,
    column: d.column ?? "backlog",
  };
}

/** Like validateDraft, but only for the fields the patch sets. */
export function validatePatch(p: BoardCardPatch): string | null {
  if (p.title !== undefined) {
    const title = p.title.trim();
    if (title.length === 0) return "board.errors.titleRequired";
    if (title.length > TITLE_MAX) return "board.errors.titleTooLong";
  }
  if (p.notes && p.notes.length > NOTES_MAX) return "board.errors.notesTooLong";
  return null;
}

/** Trims and nulls the fields the patch sets; leaves the others out. */
export function normalizePatch(p: BoardCardPatch): BoardCardPatch {
  const out: BoardCardPatch = {};
  if (p.title !== undefined) out.title = p.title.trim();
  if (p.notes !== undefined) out.notes = p.notes?.trim() ? p.notes.trim() : null;
  if (p.dueDate !== undefined) out.dueDate = p.dueDate || null;
  return out;
}

/** Stored title of a new pin: the item's title, capped, or its id if blank. */
export function pinTitle(target: { id: string; title: string }): string {
  return target.title.trim().slice(0, TITLE_MAX) || target.id;
}

/** Local calendar day as YYYY-MM-DD — due dates are DATEs, not instants. */
export function localDay(now: Date): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/** Past due and not done. `today` is a localDay() string. */
export function isOverdue(card: Pick<BoardCard, "dueDate" | "column">, today: string): boolean {
  return !!card.dueDate && card.column !== "done" && card.dueDate < today;
}
